import React, { Component } from 'react';
import { connect } from 'react-redux';
import { PropTypes as PT } from 'prop-types';
import { defineMessages, injectIntl } from 'react-intl';
import { ToastContainer } from 'react-toastify';
import _get from 'lodash/get';
import 'react-toastify/dist/ReactToastify.min.css';
import { callHistoryActions, callHistorySelectors } from '@lifesize/nucleus';
import LoadingComp from 'components/LoadingAnimation/LoadingAnimation';
import InCallModalContainer from 'containers/InCallModal/InCallModalContainer';
import IncomingCallModalComponent from 'components/IncomingCallModal/IncomingCallModal';
import FullScreenModalContainer from 'containers/FullScreenModal/FullScreenModalContainer';
import InCallContainer from 'containers/InCall/InCall/InCallContainer';
import Notification from 'components/Notification/Notification';
import NotificationLostContainer from 'containers/Notification/NotificationLostContainer';
import NotificationPillContainer from 'containers/NotificationPill/NotificationPillContainer';
import { selectIsLoggedIn } from 'selectors/auth';
import lsConfig from 'utils/global-config';
import classes from './BaseLayout.module.scss';
import { isGuestRoute } from 'utils/guest-utils';

const messages = defineMessages({
  unloadConfirmation: {
    id: 'confirmation.beforeunload',
    defaultMessage: 'Changes you made may not be saved.'
  }
});

/**
 * BaseLayout contains the highest order layout elements.
 * The children typically are going to be CoreLayout and AuthLayout.
 * The siblings to that are FullScreenModalContainer which is an overlay of the
 * current contents in the app.
 * Another sibling is InCallContainer. This holds the in call view, which is a
 * fullscreen overlay of the app as well. It also should obscure any full screen
 * modals.
 * The last is the IncomingCallModal. This will
 * lay on top of the other modals as we can receive a call at any time, so the
 * view will need to be presented on top of any current app view.
 * @param {[type]} children [description]
 */
export class BaseLayout extends Component {
  constructor(props) {
    super(props);

    this.onUnload = this.onUnload.bind(this);
  }

  componentDidMount() {
    window.addEventListener('beforeunload', this.onUnload);
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.onUnload);
  }

  onUnload(e) {
    /* Note 1: To combat unwanted pop-ups, browsers may not display prompts created in beforeunload
     * event handlers unless the page has been interacted with.
     * Note 2: You can and should handle this event through window.addEventListener() and the
     * beforeunload event.
     * Note 3: The support for custom text in the warning has been removed in nearly all browsers
     * Note 4a: Some elements/buttons (such as 'Share (invitation) by email') will unintentionally
     * trigger the beforeunload event. By adding a data attribute (data-onbeforeunload="ignore"),
     * this behavior can be ignored for individual cases
     * Note 4b: Alternatively, you may call lsConfig.skipOnBeforeUnload() to skip the very next
     * before unload event */
    // clear any missed calls if closing or reloading the site
    if (this.props.missedCalls) {
      this.props.markCallHistoryAsViewed();
    }
    if (!this.props.showUnloadWarning) {
      return;
    }
    const confirmationMessage = this.props.intl.formatMessage(messages.unloadConfirmation);
    if (_get(e, 'target.activeElement.dataset.onbeforeunload') === 'ignore' || lsConfig.settings.ignoreOnBeforeUnload) {
      lsConfig.resumeOnBeforeUnload();
      // delete e.returnValue; // eslint-disable-line no-param-reassign
      return;
    }
    // Gecko, Trident, Chrome 34+
    e.returnValue = confirmationMessage; // eslint-disable-line no-param-reassign
    // Gecko, WebKit, Chrome <34
    return confirmationMessage; // eslint-disable-line consistent-return
  }

  render() {
    if (isGuestRoute()) return null;
    return (
      <div className={classes.container}>
        <div className={classes.appContainer}>
          {!this.props.isLoggedIn && !this.props.isOutlook && <LoadingComp />}
          {this.props.children}
        </div>
        {!this.props.doNotDisturb && <IncomingCallModalComponent />}
        <FullScreenModalContainer />
        <InCallContainer />
        <InCallModalContainer />
        <Notification />
        <NotificationLostContainer />
        <NotificationPillContainer />
        <ToastContainer />
      </div>
    );
  }
}

BaseLayout.propTypes = {
  children: PT.element.isRequired,
  doNotDisturb: PT.bool,
  intl: PT.object,
  isPopoutOpen: PT.bool,
  markCallHistoryAsViewed: PT.func.isRequired,
  missedCalls: PT.number.isRequired,
  showUnloadWarning: PT.bool,
  isGuestLoaded: PT.bool
};

const mapDispatchToProps = (dispatch) => ({
  markCallHistoryAsViewed: () => dispatch(callHistoryActions.markCallHistoryAsViewed())
});

const mapStateToProps = (state) => {
  const doNotDisturb = _get(state, 'nucleus.call.doNotDisturb');
  const showUnloadWarning = !window.desktopApp;

  return {
    doNotDisturb,
    isLoggedIn: selectIsLoggedIn(state),
    missedCalls: callHistorySelectors.totalNewMissedCalls(state),
    showUnloadWarning
  };
};

const comp = connect(mapStateToProps, mapDispatchToProps)(BaseLayout);

export default injectIntl(comp);
